import React, { useState, useEffect } from 'react';
import { MdKeyboardDoubleArrowUp } from 'react-icons/md';
import DialCard from './DialCard';
import FunnelWeight from './FunnelWeight';
import Select from './Select';
import KPICard from './KPICard';
import InfoTooltip from './InfoTooltip';
import ExportDropdown from './ExportDropdown';
import { BrandLifts, Benchmark, exportMeasurementPDF, exportMeasurementExcel, downloadBlob } from '../services/measurementService';
import { percentileToDialPosition } from '../utils/positionUtils';
import './ExecutiveSummary.css';

interface ExecutiveSummaryProps {
  brandLifts?: BrandLifts;
  totalLift?: number;
  benchmarks?: Benchmark[];
  measurementId: string;
  measurementName: string;
  /** Executive summary text when no benchmark is selected; from API. */
  defaultExecutiveSummaryText?: string | null;
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({ brandLifts, totalLift, benchmarks, measurementId, measurementName, defaultExecutiveSummaryText }) => {
  // State for selected benchmark
  const [selectedBenchmark, setSelectedBenchmark] = useState<Benchmark | null>(
    benchmarks && benchmarks.length > 0 ? benchmarks[0] : null
  );

  // Update selected benchmark when benchmarks prop changes
  useEffect(() => {
    if (benchmarks && benchmarks.length > 0) {
      // If current selection is not in the new list, select the first one
      if (!selectedBenchmark || !benchmarks.find(b => b.name === selectedBenchmark.name)) {
        setSelectedBenchmark(benchmarks[0]);
      }
    } else {
      setSelectedBenchmark(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarks]);

  const getAverageAbsoluteBenchmark = (): number | undefined => {
    if (!selectedBenchmark) {
      return undefined;
    }
    const absolute = selectedBenchmark.absolute;
    return (absolute.awareness + absolute.consideration + absolute.preference + absolute.actionIntent) / 4;
  };

  const getDialPosition = (percentile?: number): number => {
    if (percentile === undefined) {
      return 50;
    }
    return percentileToDialPosition(percentile);
  };

  return (
    <div className="executive-summary">
      <div className="executive-summary-header">
        <div className="executive-summary-header-row">
          <div className="executive-summary-title-row">
            <h2 className="executive-summary-title">EXECUTIVE SUMMARY</h2>
            <InfoTooltip text="Dynamic text describing the campaign result in relation to selected benchmark." />
          </div>
          <div className="executive-summary-header-controls">
            {benchmarks && benchmarks.length > 0 && (
              <Select
                label="Compare to"
                value={selectedBenchmark?.name || null}
                options={benchmarks.map(b => ({ value: b.name, label: b.name }))}
                onChange={(benchmarkName) => {
                  const benchmark = benchmarks.find(b => b.name === benchmarkName);
                  setSelectedBenchmark(benchmark || null);
                }}
                className="select-horizontal"
                id="benchmark-select"
              />
            )}
            <ExportDropdown
              onExportPDF={async () => {
                const blob = await exportMeasurementPDF(measurementId, selectedBenchmark?.name);
                downloadBlob(blob, `${measurementName}.pdf`);
              }}
              onExportExcel={async () => {
                const blob = await exportMeasurementExcel(measurementId);
                downloadBlob(blob, `${measurementName}.xlsx`);
              }}
            />
          </div>
        </div>
        <p className="executive-summary-description">
          {selectedBenchmark?.executiveSummaryText ?? defaultExecutiveSummaryText ?? ''}
        </p>
      </div>

      <div className="performance-section">
        <div className="performance-title-row">
          <h3 className="performance-title">BRAND LIFT</h3>
          <InfoTooltip text="Lift values are in percentage points from its standing." />
        </div>

        <div className="performance-content">
          {brandLifts && (
            <div className="funnel-level-kpi-cards">
              <KPICard
                icon={MdKeyboardDoubleArrowUp}
                headline="Awareness"
                value={`+${brandLifts.awareness.toFixed(1)}%`}
                centered={true}
              />
              <KPICard
                icon={MdKeyboardDoubleArrowUp}
                headline="Consideration"
                value={`+${brandLifts.consideration.toFixed(1)}%`}
                centered={true}
              />
              <KPICard
                icon={MdKeyboardDoubleArrowUp}
                headline="Preference"
                value={`+${brandLifts.preference.toFixed(1)}%`}
                centered={true}
              />
              <KPICard
                icon={MdKeyboardDoubleArrowUp}
                headline="Action Intent"
                value={`+${brandLifts.actionIntent.toFixed(1)}%`}
                centered={true}
              />
            </div>
          )}

          <div className="performance-title-row" style={{ marginTop: '16px' }}>
            <h3 className="performance-title">BRAND PERFORMANCE VS BENCHMARK</h3>
            <InfoTooltip text="Each dial displays the percentile rank of this campaign within the selected benchmark. For example, a value of 65.0 in the Awareness dial indicates that 65% of all campaigns in the benchmark had lower lift than this campaign." />
          </div>

          {selectedBenchmark ? (
            <div className="benchmark-dials-row">
              {brandLifts && (
                <div className="funnel-level-dials">
                  <DialCard
                    value={selectedBenchmark.absolute.awareness}
                    position={getDialPosition(selectedBenchmark.absolute.awareness)}
                    label="Awareness"
                    subtext=""
                    size="compact"
                  />
                  <DialCard
                    value={selectedBenchmark.absolute.consideration}
                    position={getDialPosition(selectedBenchmark.absolute.consideration)}
                    label="Consideration"
                    subtext=""
                    size="compact"
                  />
                  <DialCard
                    value={selectedBenchmark.absolute.preference}
                    position={getDialPosition(selectedBenchmark.absolute.preference)}
                    label="Preference"
                    subtext=""
                    size="compact"
                  />
                  <DialCard
                    value={selectedBenchmark.absolute.actionIntent}
                    position={getDialPosition(selectedBenchmark.absolute.actionIntent)}
                    label="Action Intent"
                    subtext=""
                    size="compact"
                  />
                </div>
              )}
              <div className="total-lift-dial">
                <DialCard
                  value={getAverageAbsoluteBenchmark() ?? 50}
                  position={getDialPosition(getAverageAbsoluteBenchmark())}
                  label="Total Lift"
                  subtext=""
                  size="large"
                />
              </div>
            </div>
          ) : (
            <div className="benchmark-dials-row benchmark-dials-row--single">
              <div className="total-lift-dial">
                <DialCard
                  value={getAverageAbsoluteBenchmark() ?? 50}
                  position={getDialPosition(getAverageAbsoluteBenchmark())}
                  label="Total Lift"
                  subtext=""
                  size="large"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {brandLifts && (
        <div className="funnel-analysis-section">
          <div className="funnel-analysis-header">
            <div className="funnel-analysis-title-row">
              <h3 className="funnel-analysis-title">FUNNEL ANALYSIS</h3>
              <InfoTooltip text="This graph illustrates where in the brand performance funnel the campaign is most impactful. Regardless of the performance level, some campaigns have most of their impact in the top of the funnel, others in the middle and yet others in the bottom of the funnel. This graph illustrates what kind of campaign this is." />
            </div>
            <p className="funnel-analysis-description">
              Compare performance across funnel levels relative to selected benchmark.
            </p>
          </div>

          {selectedBenchmark && (
            <FunnelWeight
              awareness={selectedBenchmark.absolute.awareness}
              consideration={selectedBenchmark.absolute.consideration}
              preference={selectedBenchmark.absolute.preference}
              actionIntent={selectedBenchmark.absolute.actionIntent}
              benchmarkAwareness={selectedBenchmark.funnel.awareness}
              benchmarkConsideration={selectedBenchmark.funnel.consideration}
              benchmarkPreference={selectedBenchmark.funnel.preference}
              benchmarkActionIntent={selectedBenchmark.funnel.actionIntent}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
